import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { get, noop } from 'lodash'
import { useFortisCreateTransactionIntentionMutation } from 'api/fortis/fortisApi'
import { handleError } from 'utils/error'
import { Checkbox, FormControl, FormControlLabel, Grid, useTheme } from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import T from 'T'
import FortisSkeleton from 'components/customer-details/content/payment-details/FortisSkeleton'

// Fortis Commerce.js dynamic loader (production uses js.fortis.tech, others use js.sandbox.fortis.tech)

// Lightweight wrapper around Fortis Commerce.js Elements
// Renders a container and initializes Elements with a client token.
// Listens to the `paymentFinished` event and forwards the result to the parent.
const FortisElements = ({
  accountId,
  containerId = 'payment',
  onPaymentFinished = noop,
  onError = noop,
  amount,
  action = 'sale',
  saveMethod = false,
  onChangeSaveMethod = noop,
  SubmitButtonProps = {},
  methodType = 'all',
  submitRef,
  hideSubmitButton = false,
  showSaveMethodCheckbox = false,
  onSubmitLoadingChange = noop,
  hasSurcharge = false,
  billingAddress = {},
}) => {
  const theme = useTheme()
  const [
    requestTransactionIntention,
    { data: transactionIntentionData, isLoading: isLoadingTxn, isUninitialized: isUninitTxn, error: apiError },
  ] = useFortisCreateTransactionIntentionMutation()
  const clientToken = useMemo(() => get(transactionIntentionData, 'data.client_token', ''), [transactionIntentionData])
  const hhDescription = useMemo(() => get(transactionIntentionData, 'hhDescription', ''), [transactionIntentionData])
  const initializedRef = useRef(false)
  const elementsRef = useRef(null)
  const debounceRef = useRef(null)
  const pendingSubmitRef = useRef(false)
  // Generation guards to ignore events from stale Elements instances
  const activeInstanceIdRef = useRef(0)
  const isTokenization = useMemo(() => action === 'tokenization', [action])
  const isSale = useMemo(() => action === 'sale', [action])
  const [isSubmitLoading, setIsSubmitLoading] = useState(false)
  const [isElementsReady, setIsElementsReady] = useState(false)
  const onPaymentFinishedRef = useRef(onPaymentFinished)
  const onErrorRef = useRef(onError)

  useEffect(() => {
    onPaymentFinishedRef.current = onPaymentFinished
  }, [onPaymentFinished])

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  // Handle API errors from transaction intention request
  useEffect(() => {
    if (apiError) {
      const errorMessage = get(apiError, 'data.message') || get(apiError, 'error') || 'Failed to initialize payment form'
      const error = new Error(errorMessage)
      error.details = apiError
      // Only show toast if no error handler is provided
      if (!onErrorRef.current) {
        handleError(error)
      } else {
        onErrorRef.current(error)
      }
    }
  }, [apiError])

  // Expose imperative submit to parent via ref
  useEffect(() => {
    if (!submitRef) return
    submitRef.current = () => {
      if (isElementsReady && elementsRef?.current) {
        elementsRef.current.submit()
      } else {
        pendingSubmitRef.current = true
      }
    }
    return () => {
      submitRef.current = null
    }
  }, [submitRef, isElementsReady])
  // Ensure we call onPaymentFinished only once per submission/instance
  const finishedOnceRef = useRef(false)

  // Ensure Fortis Commerce.js is loaded before using Elements
  const [isFortisReady, setIsFortisReady] = useState(
    () => typeof window !== 'undefined' && window.Commerce && typeof window.Commerce.elements === 'function'
  )
  useEffect(() => {
    if (typeof window === 'undefined' || isFortisReady) return
    let mounted = true

    const FORTIS_SCRIPT_ID = 'fortis-commercejs'

    const finishIfReady = () => {
      if (!mounted) return
      if (window.Commerce && typeof window.Commerce.elements === 'function') {
        setIsFortisReady(true)
      }
    }

    const onLoad = () => {
      // Defer to ensure global is attached
      setTimeout(finishIfReady, 0)
    }

    const onScriptError = () => {
      const error = new Error('Failed to load Fortis Commerce.js')
      // Only show toast if no error handler is provided
      if (!onErrorRef.current) {
        handleError(error)
      } else {
        onErrorRef.current(error)
      }
    }

    let scriptEl = document.getElementById(FORTIS_SCRIPT_ID)

    if (scriptEl) {
      scriptEl.addEventListener('load', onLoad, { once: true })
      scriptEl.addEventListener('error', onScriptError, { once: true })
      // In case it already loaded before handlers attached
      setTimeout(finishIfReady, 0)
    } else {
      const src =
        process.env.REACT_APP_MODE === 'prod'
          ? 'https://js.fortis.tech/commercejs-v1.0.0.min.js'
          : 'https://js.sandbox.fortis.tech/commercejs-v1.0.0.min.js'

      scriptEl = document.createElement('script')
      scriptEl.id = FORTIS_SCRIPT_ID
      scriptEl.src = src
      scriptEl.async = true
      scriptEl.addEventListener('load', onLoad, { once: true })
      scriptEl.addEventListener('error', onScriptError, { once: true })
      document.head.appendChild(scriptEl)
    }

    return () => {
      mounted = false
    }
  }, [isFortisReady])

  const resetContainer = id => {
    const el = document.getElementById(id)
    if (!el) return
    // Important: do NOT replace the React-managed node; only clear its children
    // This avoids NotFoundError during React unmount when it tries to remove the original node.
    try {
      while (el.firstChild) {
        el.removeChild(el.firstChild)
      }
    } catch (_) {
      // Fallback to innerHTML if direct removal fails
      el.innerHTML = ''
    }
  }

  const unsubscribeAll = () => {
    const elements = elementsRef.current
    if (elements && typeof elements.off === 'function') {
      try {
        elements.off('done')
        elements.off('error')
        elements.off('submitted')
        elements.off('validationError')
        elements.off('ready')
      } catch (_) {
        // ignore
      }
    }
  }

  // Debounced fetch of client token; supports 'sale' (requires amount) and 'tokenization' (no amount)
  useEffect(() => {
    if (!isFortisReady || !accountId) {
      return
    }

    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }

    // For sale, require a positive amount; otherwise clear and exit
    if (isSale) {
      if (!amount || amount <= 0 || Number.isNaN(amount)) {
        // Unsubscribe listeners and clear current Elements iframe/content
        unsubscribeAll()
        elementsRef.current = null
        resetContainer(containerId)
        initializedRef.current = false
        activeInstanceIdRef.current = 0
        setIsElementsReady(false)
        finishedOnceRef.current = false
        pendingSubmitRef.current = false
        return
      }
    }

    // Before changing the request, unsubscribe from previous elements events
    unsubscribeAll()

    debounceRef.current = setTimeout(() => {
      const payload = isTokenization
        ? {
            accountId,
            digitalWalletsOnly: false,
            ach_sec_code: 'WEB',
            currency_code: 'USD',
            action: 'tokenization',
            method_type: methodType,
          }
        : {
            accountId,
            digitalWalletsOnly: false,
            ach_sec_code: 'WEB',
            currency_code: 'USD',
            amount,
            action: 'sale',
            save_account: saveMethod,
            method_type: methodType,
          }
      requestTransactionIntention(payload)
    }, 400)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }, [isTokenization, isSale, accountId, amount, containerId, saveMethod, requestTransactionIntention, isFortisReady, methodType])

  useEffect(() => {
    if (!clientToken || !isFortisReady) return
    // Ensure Commerce is available from the global Fortis script
    const { Commerce } = window
    if (!Commerce || typeof Commerce.elements !== 'function') {
      // Script may not have loaded yet; try again on next effect tick
      const error = new Error('Fortis Commerce.js not available')
      if (onErrorRef.current) {
        onErrorRef.current(error)
      }
      return
    }

    try {
      // Clean up any previous Elements instance and container before re-creating
      const containerSelector = `#${containerId}`
      // Unsubscribe from previous instance events before resetting/creating
      unsubscribeAll()
      resetContainer(containerId)
      setIsElementsReady(false)
      finishedOnceRef.current = false

      // Create new Elements instance
      const elements = new Commerce.elements(clientToken)
      elementsRef.current = elements

      // Get billing zip code at the time of initialization
      const billingZipCode = get(billingAddress, 'zipCode', '')

      elements.create({
        container: containerSelector,
        theme: 'default',
        language: 'en-us',
        floatingLabels: false,
        ...(process.env.REACT_APP_MODE === 'prod' ? { environment: 'production' } : {}),
        showSubmitButton: false,
        digitalWallets: [],
        showReceipt: false,
        appearance: {
          fontSize: '12px',
          colorText: theme.palette.text.secondary,
          colorButtonSelectedBackground: theme.palette.primary.main,
          colorButtonActionBackground: theme.palette.primary.main,
          colorButtonText: theme.palette.primary.main,
        },
        fields: {
          billing: hasSurcharge ? [{ name: 'postal_code', required: true, value: billingZipCode }] : false,
          additional: [{ name: 'description', value: hhDescription, hidden: true, readOnly: true }],
        },
      })

      if (typeof elements.on === 'function') {
        const onDone = result => {
          if (finishedOnceRef.current) return
          finishedOnceRef.current = true
          setIsSubmitLoading(false)
          try {
            onSubmitLoadingChange(false)
          } catch (_) {}
          if (onPaymentFinishedRef.current) onPaymentFinishedRef.current(result)
        }
        const onElementsError = errorEvent => {
          if (finishedOnceRef.current) return
          finishedOnceRef.current = true
          setIsSubmitLoading(false)
          try {
            onSubmitLoadingChange(false)
          } catch (_) {}

          // Call both handlers - onPaymentFinished for backward compatibility
          // and onError for explicit error handling
          if (onPaymentFinishedRef.current) onPaymentFinishedRef.current(errorEvent)
          if (onErrorRef.current) {
            const error = new Error(get(errorEvent, 'message', 'Payment processing error'))
            error.details = errorEvent
            onErrorRef.current(error)
          }
        }
        const onSubmitted = () => {
          finishedOnceRef.current = false
          setIsSubmitLoading(true)
          try {
            onSubmitLoadingChange(true)
          } catch (_) {}
        }
        const onValidationError = () => {
          setIsSubmitLoading(false)
          try {
            onSubmitLoadingChange(false)
          } catch (_) {}
        }
        const onReady = () => {
          setIsElementsReady(true)
          if (pendingSubmitRef.current) {
            pendingSubmitRef.current = false
            elementsRef?.current?.submit()
          }
        }
        elements.on('done', onDone)
        elements.on('error', onElementsError)
        elements.on('submitted', onSubmitted)
        elements.on('validationError', onValidationError)
        elements.on('ready', onReady)
      }
      initializedRef.current = true
    } catch (e) {
      if (!onErrorRef.current) {
        handleError(e)
      } else {
        onErrorRef.current(e)
      }
    }

    // Cleanup on unmount or before next re-create
    return () => {
      // Unsubscribe listeners first
      unsubscribeAll()
      // Fortis Elements has no official destroy API; clean up via DOM operations
      elementsRef.current = null
      resetContainer(containerId)
      setIsElementsReady(false)
      finishedOnceRef.current = false
      initializedRef.current = false
      pendingSubmitRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientToken, isFortisReady, containerId])

  // Show a skeleton while waiting for client token / Elements to be ready
  const shouldShowSkeleton =
    !clientToken && !apiError && (isLoadingTxn || isUninitTxn) && (action === 'tokenization' || (action === 'sale' && amount > 0))

  if (shouldShowSkeleton) {
    return <FortisSkeleton />
  }

  // Don't render anything if there's an API error - let parent handle it
  if (apiError) {
    return null
  }

  const handleChangeSaveMethod = event => {
    onChangeSaveMethod(event.target.checked)
  }

  return (
    <>
      <Grid container>
        <Grid size={12}>
          <div id={containerId} />
        </Grid>

        {isElementsReady && (
          <>
            {(isSale || showSaveMethodCheckbox) && (
              <Grid size={12}>
                <FormControl sx={{ my: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        tabIndex={0}
                        checked={saveMethod}
                        onChange={handleChangeSaveMethod}
                        inputProps={{ 'aria-label': T.SAVE_METHOD }}
                        sx={{
                          '&.Mui-focusVisible, &:focus-visible': theme => ({
                            outline: `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: 2,
                            borderRadius: 2,
                          }),
                        }}
                      />
                    }
                    label={T.SAVE_METHOD}
                  />
                </FormControl>
              </Grid>
            )}
            {!hideSubmitButton && (
              <Grid size={12}>
                <LoadingButton
                  {...SubmitButtonProps}
                  sx={{ mb: 2 }}
                  loading={isSubmitLoading}
                  onClick={() => elementsRef?.current?.submit()}
                  fullWidth
                  variant="contained"
                  color="primary"
                >
                  {isTokenization ? T.ADD_PAYMENT_METHOD : T.SAVE_METHOD}
                </LoadingButton>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </>
  )
}

FortisElements.propTypes = {
  amount: PropTypes.number,
  action: PropTypes.oneOf(['sale', 'tokenization']),
  containerId: PropTypes.string,
  onPaymentFinished: PropTypes.func,
  onError: PropTypes.func,
  saveMethod: PropTypes.bool,
  onChangeSaveMethod: PropTypes.func,
  SubmitButtonProps: PropTypes.object,
  accountId: PropTypes.string.isRequired,
  methodType: PropTypes.oneOf(['all', 'ach', 'cc']),
  submitRef: PropTypes.object,
  hideSubmitButton: PropTypes.bool,
  showSaveMethodCheckbox: PropTypes.bool,
  onSubmitLoadingChange: PropTypes.func,
  hasSurcharge: PropTypes.bool,
  billingAddress: PropTypes.object,
}

export default FortisElements
